import { DateTime } from 'luxon';
import { useEffect, useState } from 'react';
import { type FragmentOf, graphql, readFragment } from '@/gql/graphql';

type EventClockDirection = 'asc' | 'desc';

function getEventClockDirection(
  direction: string | null | undefined,
): EventClockDirection {
  if (direction === 'asc') {
    return 'asc';
  }

  return 'desc';
}

export function calculateElapsedMs({
  isRunning,
  startDatetime,
  elapsedSegmentMs,
  isDurationCapped = false,
  duration = 0,
}: {
  isRunning: boolean;
  startDatetime: string | null;
  elapsedSegmentMs: number | null;
  isDurationCapped?: boolean;
  duration?: number;
}): number {
  const lastKnownElapsedMs = elapsedSegmentMs ?? 0;
  let calculatedMs: number;

  if (!isRunning) {
    calculatedMs = lastKnownElapsedMs;
  } else {
    if (!startDatetime) {
      calculatedMs = lastKnownElapsedMs;
    } else {
      const clockMsDelta = DateTime.now()
        .diff(DateTime.fromISO(startDatetime), 'milliseconds')
        .as('milliseconds');
      calculatedMs = lastKnownElapsedMs + clockMsDelta;
    }
  }

  let finalMs = calculatedMs;

  if (isDurationCapped) {
    finalMs = Math.min(finalMs, duration);
  }
  finalMs = Math.max(0, finalMs);

  return finalMs;
}

const FINE_TICK_MS = 1000; // TODO: for now, everything 1 second tick; implement fine tick later
const COARSE_TICK_MS = 1000;

function calculateNextTickInfo(params: {
  currentElapsedMs: number;
  direction: 'asc' | 'desc';
  durationInMs: number;
  durationOffsetInMs: number | null;
}) {
  const { currentElapsedMs, direction, durationInMs, durationOffsetInMs } =
    params;
  const offsetToUseMs = durationOffsetInMs ?? 0;
  const now = DateTime.now();

  let nextTickRate = COARSE_TICK_MS;
  let msUntilNextTick = COARSE_TICK_MS - now.millisecond;

  if (direction === 'desc') {
    const adjustedDurationMs = Math.max(0, durationInMs - offsetToUseMs);
    const remainingMs = Math.max(0, adjustedDurationMs - currentElapsedMs);

    if (remainingMs < 60000) {
      nextTickRate = FINE_TICK_MS;
      msUntilNextTick = FINE_TICK_MS - (now.millisecond % FINE_TICK_MS);
    } else {
      msUntilNextTick = COARSE_TICK_MS - now.millisecond;
    }
  } else {
    msUntilNextTick = COARSE_TICK_MS - now.millisecond;
  }

  return { msUntilNextTick, nextTickRate };
}

export function convertDisplayTimeToElapsedMs(
  displayTime: { minutes: number; seconds: number },
  direction: 'asc' | 'desc',
  duration: number,
  durationOffset: number | null = 0,
) {
  const displayMs = displayTime.minutes * 60000 + displayTime.seconds * 1000;
  const offsetMs = durationOffset ?? 0;

  if (direction === 'desc') {
    return Math.max(0, duration - offsetMs - displayMs);
  }

  return Math.max(0, offsetMs + displayMs);
}

export function convertElapsedMsToDisplayTime(
  actualElapsedMs: number,
  direction: 'asc' | 'desc',
  durationInMs: number,
  durationOffsetInMs: number | null = 0,
): { minutes: string; seconds: string } {
  const offsetToUseMs = durationOffsetInMs ?? 0;
  let displayMs = 0;

  if (direction === 'desc') {
    const adjustedDurationMs = Math.max(0, durationInMs - offsetToUseMs);
    displayMs = Math.max(0, adjustedDurationMs - actualElapsedMs);
  } else {
    displayMs = offsetToUseMs + actualElapsedMs;
  }

  const totalSeconds = Math.floor(displayMs / 1000);
  const minutesNum = Math.floor(totalSeconds / 60);
  const secondsNum = totalSeconds % 60;

  const minutes = String(minutesNum);
  const seconds = String(secondsNum).padStart(2, '0');

  return { minutes, seconds };
}

const EventClockSegmentFragment = graphql(/* GraphQL */ `
  fragment EventClockSegment on event_segment {
    id
    duration
    duration_offset
    is_duration_capped
  }
`);

export const EventClockFragment = graphql(/* GraphQL */ `
  fragment EventClock on event {
    id
    event_clock {
      event_id
      elapsed_segment_ms
      start_datetime
      is_running
      event_segment {
        title
        ...EventClockSegment
      }
    }
    event_segment_group {
      id
      direction
    }
  }
`, [EventClockSegmentFragment]);

interface UseEventClockLogicProps {
  fragment: FragmentOf<typeof EventClockFragment> | null | undefined;
  isDisabledExternal?: boolean;
  onActualElapsedMsChange?: (actualElapsedMs: number) => void;
}

export function useEventClock({
  fragment,
  isDisabledExternal = false,
  onActualElapsedMsChange,
}: UseEventClockLogicProps) {
  const event = readFragment(EventClockFragment, fragment);
  const eventClock = event?.event_clock;

  const eventSegment = readFragment(
    EventClockSegmentFragment,
    eventClock?.event_segment,
  );

  const direction = getEventClockDirection(
    event?.event_segment_group?.direction,
  );
  const startDatetime = eventClock?.start_datetime ?? null;
  const elapsedSegmentMs = eventClock?.elapsed_segment_ms ?? 0;
  const duration = eventSegment?.duration ?? 0;
  const durationOffset = eventSegment?.duration_offset ?? 0;
  const isDurationCapped = eventSegment?.is_duration_capped ?? false;
  const isRunning = eventClock?.is_running ?? false;

  const [currentActualElapsedMs, setCurrentActualElapsedMs] = useState(() =>
    calculateElapsedMs({
      isRunning,
      startDatetime,
      elapsedSegmentMs,
      isDurationCapped,
      duration,
    }),
  );

  const isAtCap =
    isDurationCapped &&
    (direction === 'asc'
      ? currentActualElapsedMs >= duration
      : currentActualElapsedMs <= 0);

  const isEffectivelyDisabled = isDisabledExternal || isRunning || isAtCap;

  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout> | null = null;

    const tick = () => {
      const elapsedMs = calculateElapsedMs({
        isRunning,
        startDatetime,
        elapsedSegmentMs,
        isDurationCapped,
        duration,
      });

      setCurrentActualElapsedMs(elapsedMs);

      if (!isRunning) {
        if (timeoutId) clearTimeout(timeoutId);
        timeoutId = null;
        return;
      }

      if (isDurationCapped && elapsedMs >= duration) {
        if (timeoutId) clearTimeout(timeoutId);
        timeoutId = null;
        return;
      }

      const { msUntilNextTick: nextDelay } = calculateNextTickInfo({
        currentElapsedMs: elapsedMs,
        direction,
        durationInMs: duration,
        durationOffsetInMs: durationOffset,
      });
      timeoutId = setTimeout(tick, nextDelay);
    };

    const initialActualElapsedMs = calculateElapsedMs({
      isRunning,
      startDatetime,
      elapsedSegmentMs,
      isDurationCapped,
      duration,
    });

    setCurrentActualElapsedMs(initialActualElapsedMs);

    if (isRunning) {
      let shouldStartTimer = true;
      if (isDurationCapped && initialActualElapsedMs >= duration) {
        shouldStartTimer = false;
      }

      if (shouldStartTimer) {
        const { msUntilNextTick: initialDelay } = calculateNextTickInfo({
          currentElapsedMs: initialActualElapsedMs,
          direction,
          durationInMs: duration,
          durationOffsetInMs: durationOffset,
        });

        timeoutId = setTimeout(tick, initialDelay);
      } else {
        if (timeoutId) clearTimeout(timeoutId);
        timeoutId = null;
      }
    } else {
      if (timeoutId) {
        clearTimeout(timeoutId);
        timeoutId = null;
      }
    }

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [
    elapsedSegmentMs,
    startDatetime,
    isRunning,
    isDurationCapped,
    direction,
    duration,
    durationOffset,
  ]);

  const handleInputChange = (inputMinutes: number, inputSeconds: number) => {
    if (!onActualElapsedMsChange) return;

    const inputDisplayTotalMs = (inputMinutes * 60 + inputSeconds) * 1000;
    let impliedActualElapsedMs = 0;
    const offsetMs = durationOffset ?? 0;
    const segmentDurationMs = duration;

    if (direction === 'asc') {
      impliedActualElapsedMs = inputDisplayTotalMs - offsetMs;
    } else {
      const displayDuration = Math.max(0, segmentDurationMs - offsetMs);
      impliedActualElapsedMs = displayDuration - inputDisplayTotalMs;
    }

    impliedActualElapsedMs = Math.max(0, impliedActualElapsedMs);

    let validatedActualElapsedMs = impliedActualElapsedMs;
    if (isDurationCapped) {
      validatedActualElapsedMs = Math.min(
        validatedActualElapsedMs,
        segmentDurationMs,
      );
    }

    validatedActualElapsedMs = Math.max(0, validatedActualElapsedMs);

    setCurrentActualElapsedMs(validatedActualElapsedMs);
    onActualElapsedMsChange(validatedActualElapsedMs);
  };

  const displayTime = convertElapsedMsToDisplayTime(
    currentActualElapsedMs,
    direction,
    duration,
    durationOffset,
  );

  return {
    displayMinutes: displayTime.minutes,
    displaySeconds: displayTime.seconds,
    isRunning,
    isEffectivelyDisabled,
    handleInputChange,
  };
}
